import { useEffect, useMemo, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type {
  SearchFacet,
  SearchFacetValueAndCounts,
  SearchResults,
  SearchStates,
} from 'bundles/search-common/providers/searchTypes';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';

type FacetMapping = {
  [key: string]: string | string[];
};

type FacetCountsResult<T extends FacetMapping> = {
  [K in keyof T]: number;
} & {
  isLoading: boolean;
};

/**
 * Creates initial counts object with all values set to 0.
 * Used for both hook initialization and query reset scenarios.
 *
 * @param valueMapping - The mapping object from result keys to facet values
 * @returns Object with same keys as valueMapping but all values set to 0
 */
const createInitialCounts = <T extends FacetMapping>(valueMapping: T): { [K in keyof T]: number } => {
  const initialCounts = {} as { [K in keyof T]: number };
  Object.keys(valueMapping).forEach((key) => {
    initialCounts[key as keyof T] = 0;
  });
  return initialCounts;
};

/**
 * Creates a search configuration for facet-only queries.
 * Used when we need to fetch facet data separately from main search results,
 * typically when search filters are applied.
 *
 * @param query - The search query string (optional)
 * @param facetName - The specific facet to request (e.g., 'cost', 'productTypeDescription')
 * @returns SearchStates configuration object for useSearchQuery
 */
const createFacetSearchConfig = (query: string | undefined, facetName: string): SearchStates => ({
  [facetName]: {
    id: SEARCH_MAIN_INDEX_ID,
    entityType: 'PRODUCTS' as const,
    limit: 0,
    query: query || '',
    facets: [facetName],
    maxValuesPerFacet: 10,
  },
});

/**
 * Extracts counts from facet data based on value mapping.
 * Handles both single values and arrays of values (for aggregation).
 *
 * @param targetFacet - The facet object containing valuesAndCounts
 * @param valueMapping - Mapping from result keys to facet values (string or string[])
 * @returns Object with counts for each mapped key
 *
 * @example
 * // Single values
 * extractCountsFromFacet(costFacet, { freeCount: 'Free', paidCount: 'Paid' })
 * // => { freeCount: 10, paidCount: 15 }
 *
 * // Aggregated values
 * extractCountsFromFacet(typeFacet, { credentialsCount: ['Certificates', 'Specializations'] })
 * // => { credentialsCount: 18 } (sum of both values)
 */
const extractCountsFromFacet = <T extends FacetMapping>(
  targetFacet: SearchFacet,
  valueMapping: T
): { [K in keyof T]: number } => {
  const newCounts = {} as { [K in keyof T]: number };

  Object.entries(valueMapping).forEach(([key, values]) => {
    const valuesToSearch = Array.isArray(values) ? values : [values];
    let totalCount = 0;

    valuesToSearch.forEach((value) => {
      const entry = targetFacet.valuesAndCounts?.find((item: SearchFacetValueAndCounts) => item.value === value);
      if (entry) {
        totalCount += entry.count;
      }
    });

    newCounts[key as keyof T] = totalCount;
  });

  return newCounts;
};

/**
 * Determines if facet processing should proceed based on loading state and data availability.
 * Facet processing should only happen when:
 * 1. Not currently loading
 * 2. Haven't initialized yet
 * 3. Have either main facets (no filters) OR separate facet results (with filters)
 *
 * @param isLoading - Whether search is currently loading
 * @param hasInitialized - Whether we've already processed facets for current query
 * @param searchData - Main search data that may contain facetFilters
 * @param facets - Facets from main search results
 * @param facetResults - Results from separate facet-only query
 * @returns True if facet processing should proceed
 */
const shouldProcessFacets = (
  isLoading: boolean,
  hasInitialized: boolean,
  searchData: { facetFilters?: unknown[] } | null | undefined,
  facets: SearchFacet[] | null | undefined,
  facetResults: SearchResults | undefined
): boolean => {
  if (isLoading || hasInitialized) {
    return false;
  }

  // Check if we have facets from main search (no filters) or from separate facet query (with filters)
  const hasMainFacets = !searchData?.facetFilters?.length && Boolean(facets);
  const hasFacetResults = Boolean(searchData?.facetFilters?.length && facetResults?.[0]);

  return hasMainFacets || hasFacetResults;
};

/**
 * Generic hook to extract facet counts from search results.
 *
 * This hook provides a unified way to extract and aggregate counts from any search facet.
 * It automatically handles:
 * - Fetching the current search query from URL parameters
 * - Using main search facets when no filters are applied
 * - Making separate facet-only requests when search filters are active
 * - Resetting counts when the search query changes
 * - Aggregating multiple facet values into single counts
 *
 * @param facetName - The name of the facet to extract counts from (e.g., 'cost', 'productTypeDescription')
 * @param valueMapping - Object mapping result keys to facet values
 *   - Keys become the properties in the returned object
 *   - Values can be strings (single facet value) or arrays (aggregated values)
 *
 * @returns Object containing:
 *   - Named count properties (based on valueMapping keys)
 *   - isLoading: boolean indicating if counts are being fetched
 *
 * @example
 * // Extract cost facet counts
 * const { freeCount, paidCount, isLoading } = useFacetCounts('cost', {
 *   freeCount: 'Free',
 *   paidCount: 'Paid'
 * });
 *
 * @example
 * // Aggregate multiple values
 * const { credentialsCount, coursesCount } = useFacetCounts('productTypeDescription', {
 *   credentialsCount: ['Professional Certificates', 'Specializations'],
 *   coursesCount: 'Courses'
 * });
 */
const useFacetCounts = <T extends FacetMapping>(facetName: string, valueMapping: T): FacetCountsResult<T> => {
  const [counts, setCounts] = useState<{ [K in keyof T]: number }>(() => createInitialCounts(valueMapping));

  const [hasInitialized, setHasInitialized] = useState(false);
  const [currentQuery, setCurrentQuery] = useState<string | undefined>();

  const query = inferQueryFromParams(useLocation().query.query);
  const { getSearchResults, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const facets = searchData?.facets;

  const searchConfig = useMemo(() => createFacetSearchConfig(query, facetName), [query, facetName]);

  // Get counts from main index or make additional request if filters are applied
  const { results: facetResults } = useSearchQuery(searchConfig, {
    skip: hasInitialized || (searchData && !searchData?.facetFilters?.length),
  });

  const effectiveFacets = useMemo(() => {
    return facetResults?.[0]?.facets || facets;
  }, [facetResults, facets]);

  // Reset state when query changes
  useEffect(() => {
    if (query !== currentQuery) {
      setHasInitialized(false);
      setCounts(createInitialCounts(valueMapping));
      setCurrentQuery(query);
    }
  }, [query, currentQuery, valueMapping]);

  // Extract facet counts
  useEffect(() => {
    if (shouldProcessFacets(isLoading ?? false, hasInitialized, searchData, facets, facetResults)) {
      const targetFacet = effectiveFacets?.find((facet: SearchFacet) => facet.name === facetName);
      if (targetFacet?.valuesAndCounts) {
        const newCounts = extractCountsFromFacet(targetFacet, valueMapping);
        setCounts(newCounts);
        setHasInitialized(true);
      }
    }
  }, [isLoading, facets, hasInitialized, searchData, facetResults, effectiveFacets, facetName, valueMapping]);

  return {
    ...counts,
    isLoading: (isLoading ?? false) && !hasInitialized,
  } as FacetCountsResult<T>;
};

export default useFacetCounts;
